import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { AdminPageWrapper } from "../AdminPageWrapper";
import {
  HStack,
  Stack,
  Text,
  Table,
  Spinner,
  IconButton,
  ButtonGroup,
  useDisclosure,
} from "@chakra-ui/react";
import { AdminView } from "../../models/AdminView";
import { toast } from "../../../shared/views/CustomToaster";
import { PageControls } from "../../../pages/map/views/BottomBar/cra-way/PageControls";
import { Page } from "../../../shared/utils/http-utils";
import { DeleteIcon, RefreshIcon } from "../../../shared/views/icons";
import { AdminUser } from "../../models/AdminUser";
import { AdminUserApi } from "../../services/AdminUserApi";
import { TransferUserReferencesModal } from "./TransferUserReferencesModal";

const pageSize = 20;

const AdminUsersPageBase: React.FC = () => {
  const [pageIndex, setPageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string>();

  const transferUserReferencesModal = useDisclosure();

  const [page, setPage] = useState<Page<AdminUser>>();

  const goToPage = async (pageNumber: number) => {
    setConfirmDeleteId(undefined);
    setLoading(true);
    const usersPage = await AdminUserApi.page(pageSize, pageNumber).then(
      (res) => res.data
    );
    setPage(usersPage);
    setLoading(false);
  };

  useEffect(() => {
    goToPage(pageIndex);
  }, [pageIndex]);

  return (
    <AdminPageWrapper selectedView={AdminView.Users}>
      <Stack px={6}>
        <Text fontSize="2xl">{AdminView.Users}</Text>

        <HStack justifyContent={"end"}>
          <PageControls
            pageSize={pageSize}
            pageIndex={pageIndex}
            setPageIndex={setPageIndex}
            nItems={page?.meta?.totalElements ?? 0}
          />
        </HStack>

        {loading ? (
          <Spinner color={"primary.500"} />
        ) : (
          <Table.Root striped>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Email</Table.ColumnHeader>
                <Table.ColumnHeader>Created at</Table.ColumnHeader>
                <Table.ColumnHeader textAlign={"right"}>
                  Actions
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {page?.data.map((user) => (
                <Table.Row key={user.id}>
                  <Table.Cell>{user.email}</Table.Cell>
                  <Table.Cell>{user.createdAt.substring(0, 10)}</Table.Cell>
                  <Table.Cell textAlign={"right"} py={0}>
                    <ButtonGroup size="sm">
                      <IconButton
                        title="Re-send invitation"
                        variant="outline"
                        colorPalette={"primary"}
                        onClick={() => {
                          AdminUserApi.resendInvitation(user.id).then(() =>
                            toast("success", "Invitation sent")
                          );
                        }}
                      >
                        <RefreshIcon />
                      </IconButton>

                      <IconButton
                        title="Delete"
                        variant={
                          confirmDeleteId === user.id ? "solid" : "outline"
                        }
                        colorPalette={"red"}
                        onClick={
                          confirmDeleteId === user.id
                            ? transferUserReferencesModal.onOpen
                            : () => setConfirmDeleteId(user.id)
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ButtonGroup>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}
      </Stack>

      {transferUserReferencesModal.open && (
        <TransferUserReferencesModal
          onClose={transferUserReferencesModal.onClose}
          onSubmit={async (targetEmail) => {
            await AdminUserApi.transferReferences(confirmDeleteId!, targetEmail)
              .then(() => AdminUserApi.delete(confirmDeleteId!))
              .then(() => {
                toast("success", "Done");
                transferUserReferencesModal.onClose();
                goToPage(pageIndex);
              })
              .catch((e) => {
                console.error(e);
                toast("error", "Unable to delete user");
              });
          }}
        />
      )}
    </AdminPageWrapper>
  );
};

export const AdminUsersPage = observer(AdminUsersPageBase);
